import React, { useCallback, useEffect, useState } from 'react';
import { notification } from 'antd';

import ApiConnectionsContext from '@totem/components/administration/apiConnections/ApiConnectionsContext';
import {
  ApiConnection,
  ApiConnectionStatus,
  ApiConnectionSummary,
  ApiConnectionTypeField,
} from '@totem/components/administration/apiConnections/types';
import { getToken } from '@totem/components/userContext/accountUtilities';
import {
  API_CONNECTION_TYPE_FIELDS_ENDPOINT,
  API_CONNECTIONS_ENDPOINT,
} from '@totem/utilities/endpoints';

type Props = {
  children: React.ReactNode;
};

const ApiConnectionsContainer = ({ children }: Props) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ApiConnectionSummary[]>([]);
  const [typeFields, setTypeFields] = useState<ApiConnectionTypeField[]>([]);
  const [editingConnection, setEditingConnection] =
    useState<ApiConnection | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);

    fetch(API_CONNECTIONS_ENDPOINT, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    })
      .then((res) => res.json())
      .then((records: ApiConnectionSummary[]) => {
        setData(records ?? []);
      })
      .catch(() => {
        notification.error({ message: 'Failed to load API connections.' });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    fetch(API_CONNECTION_TYPE_FIELDS_ENDPOINT, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    })
      .then((res) => res.json())
      .then((records: ApiConnectionTypeField[]) => {
        setTypeFields(records ?? []);
      })
      .catch(() => {
        notification.error({
          message: 'Failed to load connection type fields.',
        });
      });
  }, []);

  const onAdd = () => {
    setEditingConnection(null);

    setModalOpen(true);
  };

  const onEdit = (id: string) => {
    setEditLoading(true);

    setModalOpen(true);

    fetch(`${API_CONNECTIONS_ENDPOINT}/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load connection');

        return res.json();
      })
      .then((record: ApiConnection) => {
        setEditingConnection(record);
      })
      .catch(() => {
        notification.error({ message: 'Failed to load connection details.' });

        setModalOpen(false);
      })
      .finally(() => setEditLoading(false));
  };

  const onModalClose = () => {
    setModalOpen(false);

    setEditingConnection(null);
  };

  const onSave = (connection: Partial<ApiConnection>) => {
    const isNew = !connection.id;
    const url = isNew
      ? API_CONNECTIONS_ENDPOINT
      : `${API_CONNECTIONS_ENDPOINT}/${connection.id}`;
    const method = isNew ? 'POST' : 'PUT';

    const body = isNew
      ? connection
      : {
          displayName: connection.displayName,
          organization: connection.organization,
          data: connection.data,
        };

    fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(body),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Save failed');
        notification.success({
          message: isNew
            ? 'API connection created.'
            : 'API connection updated.',
        });

        onModalClose();

        refresh();
      })
      .catch(() => {
        notification.error({ message: 'Failed to save API connection.' });
      });
  };

  const onStatusChange = (id: string, status: ApiConnectionStatus) => {
    fetch(`${API_CONNECTIONS_ENDPOINT}/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ status }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Status update failed');
        notification.success({ message: 'Connection status updated.' });

        refresh();
      })
      .catch(() => {
        notification.error({ message: 'Failed to update connection status.' });
      });
  };

  const onDelete = (id: string) => {
    fetch(`${API_CONNECTIONS_ENDPOINT}/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Delete failed');
        notification.success({ message: 'API connection deleted.' });

        refresh();
      })
      .catch(() => {
        notification.error({ message: 'Failed to delete API connection.' });
      });
  };

  return (
    <ApiConnectionsContext.Provider
      value={{
        loading,
        data,
        typeFields,
        editingConnection,
        editLoading,
        modalOpen,
        onRefresh: refresh,
        onAdd,
        onEdit,
        onDelete,
        onStatusChange,
        onSave,
        onModalClose,
      }}
    >
      {children}
    </ApiConnectionsContext.Provider>
  );
};

export default ApiConnectionsContainer;
